import React, { useEffect, useMemo, useState } from 'react';
import { AccordionDetails, Chip, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  AccountData,
  BulkRedeemSession,
  OrderPageTypes,
  OrderService,
  PassSessionType,
  TimeSlotResponseOut,
} from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { getOrderPage } from '../../../../../utils/AccountUtils';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { getDateFormat } from '../../../../../common/getDateAndTimeFormat';
import ToggleDays from '../../../../../common/ui/ToggleDays';
import { SharedMobileTimePicker } from '../../../../../common/ui/SharedMobileTimePicker';
import { Accordion, AccordionSummary } from '../Accordion';
import { EditOrderPanels } from '../../EditOrderTypes';
import { CalendarMonth } from './CalendarMonth';
import { buildEventSessions, DAY_KEYS } from './bulkSessionsUtils';

type PrivateSession = { date: moment.Moment; time: moment.Moment; dateOpen: boolean };

type BulkSessionsPanelProps = {
  orderPageId: string;
  onCheckedSessionsChange: (sessions: BulkRedeemSession[]) => void;
  remainingPunches: number;
  expanded?: EditOrderPanels;
  handleChange: (panel: EditOrderPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
};

export const BulkSessionsPanel = ({
  orderPageId,
  onCheckedSessionsChange,
  remainingPunches,
  expanded,
  handleChange,
}: BulkSessionsPanelProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { longDateFormat: dateFormat } = getDateFormat(account);
  const activity = account?.activity_label_plural?.[language] ?? t('passes.activity_fallback_plural');

  const [dateSlots, setDateSlots] = useState<Map<string, TimeSlotResponseOut[]>>(new Map());
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [privateSessions, setPrivateSessions] = useState<PrivateSession[]>([
    { date: moment(), time: moment().set({ hour: 9, minute: 0, second: 0 }), dateOpen: false },
  ]);
  const [preferredTimeByDay, setPreferredTimeByDay] = useState<Map<number, string>>(new Map());

  const rangeStart = useMemo(() => moment().startOf('day'), []);
  const rangeEnd = useMemo(() => moment().add(8, 'weeks').endOf('day'), []);

  const orderPage = getOrderPage(account, orderPageId);
  const isPrivate = orderPage?.order_page_type === OrderPageTypes.PRIVATE;

  useEffect(() => {
    if (!orderPageId || !orderPage) return;
    if (isPrivate) {
      setDateSlots(new Map());
      setSelectedDates(new Set());
      setFetched(true);
      onCheckedSessionsChange(
        privateSessions.map((s) => ({
          order_page_id: orderPageId,
          type: PassSessionType.PRIVATE,
          date: s.date.format('YYYY-MM-DD'),
          time: s.time.hours() * 3600 + s.time.minutes() * 60,
        })),
      );
      return;
    }
    if (![OrderPageTypes.EVENT, OrderPageTypes.COURSE].includes(orderPage.order_page_type as OrderPageTypes)) return;

    void (async () => {
      setLoading(true);
      setFetched(false);
      try {
        const dates = await OrderService.getAvailableDatesAdmin({
          account_id: account.id,
          order_page_id: orderPageId,
          order_details: { persons: 0, duration: orderPage.duration, price: 0, rooms: 0 },
        });

        const slots = new Map<string, TimeSlotResponseOut[]>();
        dates.forEach((d) => {
          if (!d.isDisabled && moment(d.date).isSameOrAfter(rangeStart)) {
            const available = d.times.filter((slot) => !slot.isDisabled);
            if (available.length > 0) slots.set(d.date, available);
          }
        });

        const allDates = Array.from(slots.keys());
        const capped = new Set<string>();
        for (const date of allDates) {
          if (capped.size >= remainingPunches) break;
          capped.add(date);
        }

        setDateSlots(slots);
        setSelectedDates(capped);
        onCheckedSessionsChange(buildEventSessions(orderPageId, capped, slots, preferredTimeByDay));
      } finally {
        setLoading(false);
        setFetched(true);
      }
    })();
  }, [orderPageId]);

  const availableDates = useMemo(() => new Set(dateSlots.keys()), [dateSlots]);

  const effectiveRangeEnd = useMemo(() => {
    if (availableDates.size === 0) return rangeEnd;
    const last = Array.from(availableDates).sort().at(-1)!;
    return moment(last).endOf('day');
  }, [availableDates, rangeEnd]);

  const disabledDates = useMemo(() => {
    const disabled = new Set<string>();
    if (selectedDates.size >= remainingPunches) {
      availableDates.forEach((dateStr) => {
        if (!selectedDates.has(dateStr)) disabled.add(dateStr);
      });
    }
    return disabled;
  }, [availableDates, selectedDates, remainingPunches]);

  // For each day-of-week present in selectedDates, collect the union of available time strings
  // across all selected dates of that day. Only include days where at least one date has >1 slot.
  const daysWithMultipleSlots = useMemo(() => {
    const result = new Map<number, string[]>();
    selectedDates.forEach((dateStr) => {
      const slots = dateSlots.get(dateStr);
      if (!slots || slots.length <= 1) return;
      const dayIndex = moment(dateStr).day();
      const existing = result.get(dayIndex) ?? [];
      const newTimes = slots.map((s) => moment.unix(s.seconds).utc().format('HH:mm'));
      result.set(dayIndex, [...new Set([...existing, ...newTimes])].sort());
    });
    return result;
  }, [selectedDates, dateSlots]);

  // Auto-populate default preference (first time) for newly visible days; clean up removed days.
  useEffect(() => {
    setPreferredTimeByDay((prev) => {
      const next = new Map(prev);
      daysWithMultipleSlots.forEach((times, dayIndex) => {
        if (!next.has(dayIndex)) next.set(dayIndex, times[0]);
      });
      prev.forEach((_, dayIndex) => {
        if (!daysWithMultipleSlots.has(dayIndex)) next.delete(dayIndex);
      });
      return next;
    });
  }, [daysWithMultipleSlots]);

  // Propagate updated sessions whenever the time preference changes.
  useEffect(() => {
    if (!fetched || isPrivate) return;
    onCheckedSessionsChange(buildEventSessions(orderPageId, selectedDates, dateSlots, preferredTimeByDay));
  }, [preferredTimeByDay]);

  const handleToggleDate = (dateStr: string) => {
    const next = new Set(selectedDates);
    if (next.has(dateStr)) {
      next.delete(dateStr);
    } else {
      if (selectedDates.size >= remainingPunches) return;
      next.add(dateStr);
    }
    setSelectedDates(next);
    onCheckedSessionsChange(buildEventSessions(orderPageId, next, dateSlots, preferredTimeByDay));
  };

  const activeDays: number[] = useMemo(() => {
    const days = new Set<number>();
    selectedDates.forEach((d) => days.add(moment(d).day()));
    return Array.from(days);
  }, [selectedDates]);

  const handleSetDays = (newDays: number[]) => {
    const added = newDays.filter((d) => !activeDays.includes(d));
    const removed = activeDays.filter((d) => !newDays.includes(d));
    const next = new Set(selectedDates);
    availableDates.forEach((dateStr) => {
      const dow = moment(dateStr).day();
      if (removed.includes(dow)) {
        next.delete(dateStr);
      } else if (added.includes(dow)) {
        if (next.size < remainingPunches) next.add(dateStr);
      }
    });
    setSelectedDates(next);
    onCheckedSessionsChange(buildEventSessions(orderPageId, next, dateSlots, preferredTimeByDay));
  };

  const monthsToShow = useMemo(() => {
    const months: { year: number; month: number }[] = [];
    const cur = moment(rangeStart).startOf('month');
    while (cur.isSameOrBefore(effectiveRangeEnd, 'month')) {
      months.push({ year: cur.year(), month: cur.month() });
      cur.add(1, 'month');
    }
    return months;
  }, [rangeStart, effectiveRangeEnd]);

  const updatePrivateSessions = (sessions: PrivateSession[]) => {
    setPrivateSessions(sessions);
    onCheckedSessionsChange(
      sessions.map((s) => ({
        order_page_id: orderPageId,
        type: PassSessionType.PRIVATE,
        date: s.date.format('YYYY-MM-DD'),
        time: s.time.hours() * 3600 + s.time.minutes() * 60,
      })),
    );
  };

  const addPrivateSession = () => {
    const last = privateSessions[privateSessions.length - 1];
    updatePrivateSessions([
      ...privateSessions,
      {
        date: last ? last.date.clone().add(1, 'day') : moment(),
        time: last ? last.time.clone() : moment().set({ hour: 9, minute: 0, second: 0 }),
        dateOpen: false,
      },
    ]);
  };

  const removePrivateSession = (index: number) => {
    updatePrivateSessions(privateSessions.filter((_, i) => i !== index));
  };

  const summaryCount = isPrivate ? privateSessions.length : selectedDates.size;

  return (
    <Accordion
      expandedPanelName={expanded}
      panelName={EditOrderPanels.BulkSessionsPanel}
      onChange={handleChange}
      allOpenValue={EditOrderPanels.AllOpen}>
      <AccordionSummary
        title={t('passes.bulk_sessions_title', { activities: activity })}
        summaryComponent={
          <Typography variant="h6" color="primary" fontWeight={700}>
            {summaryCount}
            {!isPrivate && ` / ${remainingPunches === Infinity ? '∞' : remainingPunches}`}
          </Typography>
        }
      />
      <AccordionDetails>
        {loading && (
          <Grid container justifyContent="center" p={1}>
            <CircularProgress size={24} />
          </Grid>
        )}

        {!loading && isPrivate && (
          <Grid container flexDirection="column" gap={1}>
            {privateSessions.map((session, index) => (
              <Grid container key={index} alignItems="center" gap={1}>
                <Grid item xs={5}>
                  <DatePicker
                    open={session.dateOpen}
                    onOpen={() =>
                      setPrivateSessions((prev) => prev.map((s, i) => (i === index ? { ...s, dateOpen: true } : s)))
                    }
                    onClose={() =>
                      setPrivateSessions((prev) => prev.map((s, i) => (i === index ? { ...s, dateOpen: false } : s)))
                    }
                    label={`${t('passes.session')} ${index + 1}`}
                    value={session.date}
                    format={dateFormat}
                    onChange={(val) =>
                      val &&
                      updatePrivateSessions(privateSessions.map((s, i) => (i === index ? { ...s, date: val } : s)))
                    }
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <SharedMobileTimePicker
                    label={t('calendar.edit_course.start_time')}
                    value={session.time}
                    minutesStep={15}
                    onChange={(val) =>
                      val &&
                      updatePrivateSessions(privateSessions.map((s, i) => (i === index ? { ...s, time: val } : s)))
                    }
                  />
                </Grid>
                <Grid item>
                  <IconButton
                    onClick={() => removePrivateSession(index)}
                    disabled={privateSessions.length === 1}
                    size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            {privateSessions.length < remainingPunches && (
              <Grid>
                <IconButton onClick={addPrivateSession} color="primary" size="small">
                  <AddIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        )}

        {!loading && !isPrivate && fetched && availableDates.size === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('passes.no_upcoming_sessions', { activities: activity })}
          </Typography>
        )}

        {!loading && !isPrivate && fetched && availableDates.size > 0 && (
          <Grid container flexDirection="column" gap={1.5}>
            <ToggleDays days={activeDays} setDays={handleSetDays} />
            <Grid container spacing={2}>
              {monthsToShow.map(({ year, month }) => (
                <Grid item key={`${year}-${month}`} xs={12} sm={6}>
                  <CalendarMonth
                    year={year}
                    month={month}
                    availableDates={availableDates}
                    selectedDates={selectedDates}
                    disabledDates={disabledDates}
                    onToggleDate={handleToggleDate}
                    rangeStart={rangeStart}
                    rangeEnd={effectiveRangeEnd}
                  />
                </Grid>
              ))}
            </Grid>
            {daysWithMultipleSlots.size > 0 && (
              <Grid container flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('passes.preferred_time')}
                </Typography>
                {Array.from(daysWithMultipleSlots.entries())
                  .sort(([a], [b]) => a - b)
                  .map(([dayIndex, times]) => (
                    <Grid container alignItems="center" gap={1} key={dayIndex}>
                      <Typography variant="caption" sx={{ minWidth: 90, fontWeight: 600 }}>
                        {t(`days.${DAY_KEYS[dayIndex]}`)}:
                      </Typography>
                      {times.map((time) => (
                        <Chip
                          key={time}
                          label={time}
                          size="small"
                          color={preferredTimeByDay.get(dayIndex) === time ? 'primary' : 'default'}
                          onClick={() => setPreferredTimeByDay((prev) => new Map(prev).set(dayIndex, time))}
                          clickable
                        />
                      ))}
                    </Grid>
                  ))}
              </Grid>
            )}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
